import { Component, Inject } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { TranslationService } from '@sdbn/common';

import { HttpService } from '@sdbn/appclient';
import { ServiceResponse } from '@sdbn/appclient';
import { ResponseMessage } from '@sdbn/appclient';
import { DialogService } from '@sdbn/appclient';

import { environment } from 'src/environments/environment';
import { CAssignVehicleToStation, CheckSeResponse, CleanTransportDetails } from '../clean-transport.model';
import { StructureSelectionService } from 'src/tmc-yard/common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { StructureSelection } from 'src/tmc-yard/common/structure-selection/structure-selection.model';
import { Station } from 'src/tmc-yard/client/model/station';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';

export interface CleanTransportDialogData {
  transportId: number
}

@Component({
  selector: 'app-clean-transport',
  templateUrl: './clean-transport.component.html',
  styleUrls: ['./clean-transport.component.scss']
})
export class CleanTransportComponent {

  assignVehicleToStationVisible = false;
  vehicleTargetStation: Station;

  isLoading = false;
  responseMessages: ResponseMessage[];

  cleanTransportDisabled = true;
  cleanTransportFeatureAvailable = true;

  optionsFormGroup = new FormGroup({});

  archiveTransportEnabled = false;
  assignVehicleToStationEnabled = false;

  confirmEnabled = false;

  formUpdating = false;;

  constructor(
    public dialogRef: MatDialogRef<CleanTransportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CleanTransportDialogData,
    public translationService: TranslationService,
    private structureSelectionService: StructureSelectionService,
    private httpService: HttpService,
    private dialog: MatDialog,
    private tmcContextService: TmcContextService,
    private dialogService: DialogService
  ) {
    this.optionsFormGroup.addControl('cleanupAction', new FormControl('', [Validators.required]))
    this.optionsFormGroup.addControl('vehicleTargetStationName', new FormControl('', [Validators.required]))

    this.optionsFormGroup.valueChanges.subscribe(
      values => {
        this.confirmEnabled = this.optionsFormGroup.valid;
        this.assignVehicleToStationVisible = this.isAssignVehicleToStationSelected();;
        // if (!this.formUpdating) {
        this.updateEnablement();
        // }
      }
    );
    this.httpService.postWithContext<CleanTransportDetails>(environment.cleanTransportEndpoint + '/details', data.transportId).subscribe(
      details => {
        this.assignVehicleToStationEnabled = details.assignVehicleToStationEnabled;
        this.archiveTransportEnabled = details.archiveTransportEnabled;
      }
    );
  }

  public cancel() {
    this.dialogRef.close();
  }

  public confirm() {
    if (this.isArchiveTransportSelected()) {
      this.archiveTransport().subscribe(
        response => {
          if (response) {
            this.dialogRef.close('cleaned');
          }
        }
      );
    } else if (this.isAssignVehicleToStationSelected()) {
      this.assignVehicleToStation();
    }
  }

  public assignVehicleTargetStation() {

    const structureSelection = new StructureSelection();

    structureSelection.stationId = this.vehicleTargetStation?.id;

    this.structureSelectionService.selectStation(structureSelection).subscribe(
      value => {

        if (value != null) {
          const selection: StructureSelection = value;
          if (selection.stationId != null) {
            this.vehicleTargetStation = this.tmcContextService.findStationById(selection.stationId);
          } else {
            this.vehicleTargetStation = null;
          }
          this.optionsFormGroup.patchValue({ vehicleTargetStationName: this.vehicleTargetStation?.name })
        }
      }
    );
  }

  public archiveTransport(): Subject<ServiceResponse> {
    const sub = new Subject<ServiceResponse>();

    this.httpService.postWithContext<CheckSeResponse>(environment.cleanTransportEndpoint + '/check-se', this.data.transportId).subscribe(
      seResponse => {
        if (seResponse.hasEquipment) {

          this.dialogService.showInfoDialog(
            'tmc.clean-transport.supplementary-equipment.message-dialog.title',
            'tmc.clean-transport.supplementary-equipment.message-dialog.message'
          ).afterClosed().subscribe( close => {
            sub.next(null);
            sub.complete();
          });

        } else {
          this.dialogService.showConfirm('tmc.clean-transport.clean.confirm-dialog.title', 'tmc.clean-transport.clean.confirm-dialog.message').afterClosed().subscribe(
            confirmed => {
              if (confirmed) {
                this.performCleanup(this.data.transportId).subscribe(
                  next => {
                    sub.next(next);
                    sub.complete();
                  },
                  error => {
                    sub.error(error);
                  });
              } else {
                sub.next(null);
                sub.complete();
              }
            }
          );
        }
      }, error => {
        sub.error(error);
      }
    );

    return sub;
  }

  private updateEnablement() {

    if (this.formUpdating) {
      return;
    }

    this.formUpdating = true;
    try {
      if (this.isAssignVehicleToStationSelected()) {
        this.optionsFormGroup.controls.vehicleTargetStationName.enable({ onlySelf: false, emitEvent: true });
      } else {
        this.optionsFormGroup.controls.vehicleTargetStationName.disable({ onlySelf: false, emitEvent: true });
      }
    } finally {
      this.formUpdating = false;
    }
  }

  private assignVehicleToStation() {

    if (this.data.transportId == null || this.vehicleTargetStation == null) {
      return;
    }

    const dto = new CAssignVehicleToStation();
    dto.transportId = this.data.transportId;
    dto.stationId = this.vehicleTargetStation.id;

    this.httpService.postWithContext<ServiceResponse>(environment.cleanTransportEndpoint + '/assign-vehicle-to-station', dto).subscribe(
      response => {
        if (ServiceResponse.isFailed(response)) {
          this.responseMessages = response.responseMessages;
        } else {
          this.responseMessages = new Array<ResponseMessage>();
          this.dialogRef.close();
        }
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
      }
    );
  }

  private performCleanup(transportId: number): Observable<ServiceResponse> {
    return this.httpService.postWithContext<ServiceResponse>(environment.cleanTransportEndpoint + '/archive-transport', transportId);
  }

  private isAssignVehicleToStationSelected(): boolean {
    return this.optionsFormGroup.controls.cleanupAction.value === 'assignVehicleToStation';
  }

  private isArchiveTransportSelected(): boolean {
    return this.optionsFormGroup.controls.cleanupAction.value === 'archiveTransport';
  }
}
